import { withApollo } from '@jusbrasil-web/core-apollo'
import { FilterInput, JurisprudenceMetadata } from '@jusbrasil-web/shared-apollo-client/types'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  FEATURE_NAME,
  GA4ClickEvent,
  GA4InputSubmitEvent,
  GA4ViewItemListEvent,
  INPUT_METHOD,
} from '@jusbrasil-web/shared-ga4-events'
import { useGTM } from '@jusbrasil-web/shared-gtm'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useCallback, useEffect } from 'react'

import { ThesisChatLayout, ThesisHead } from '../../components'
import { CHAT_VERSION_DOCUMENT_LIMIT } from '../../constants'
import { getClusterQueryFilter } from '../thesis-cluster/cluster-filter'
import {
  TesesJuridicasChatRootQuery,
  TesesJuridicasChatRootQueryVariables,
} from './__generated__/query.graphql'
import { buildMessages } from './messages'
import { TesesJuridicasChatRoot } from './query'

export interface TesesJuridicasChatPageProps {
  clusterLabel: string
  data: TesesJuridicasChatRootQuery
  clusterId: string
  canonicalUrl: string
}

export type TesesJuridicasChatPageParams = {
  id: string
}

export function TesesJuridicasChatPage(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { data, clusterLabel, canonicalUrl, clusterId } = props

  const gtm = useGTM()
  const { sendGA4Event } = useGA4Dispatcher()

  useEffect(() => {
    gtm.push({
      ga4_resource_id: clusterId,
      ga4_content_subgroup: 'teses-chat',
    })
  }, [gtm, clusterId])

  const onDisplayMessages = useCallback(() => {
    sendGA4Event(
      new GA4ViewItemListEvent({
        renders: data.root?.searchHaystack?.items.map((document) => ({
          item_variant: 'static_reference',
          item_name: 'cluster_theses_jurisprudence',
          item_brand: 'result_snippet',
          item_id: `JURISPRUDENCIA:${(document.data as JurisprudenceMetadata).docId}`,
        })),
      })
    )
  }, [data.root?.searchHaystack?.items, sendGA4Event])

  const onSubmitInput = useCallback(() => {
    sendGA4Event(
      new GA4InputSubmitEvent({
        featureClass: 'thesis_chat_landing',
        featureName: FEATURE_NAME.MESSAGE_SUBMIT,
        inputMethod: INPUT_METHOD.FREE_TEXT,
        successfulAttempt: true,
      })
    )
  }, [sendGA4Event])

  const onAnswerCTAClick = useCallback(() => {
    sendGA4Event(
      new GA4ClickEvent({
        componentClass: 'thesis_chat_landing',
        componentName: 'cta',
        resourceId: clusterId,
        selectedOption: 'show_references',
      })
    )
  }, [clusterId, sendGA4Event])

  const messages = buildMessages({ data, clusterLabel, clusterId, onAnswerCTAClick })

  return (
    <div>
      <ThesisHead canonicalUrl={canonicalUrl} clusterLabel={clusterLabel} chatVersion />
      <ThesisChatLayout
        clusterId={clusterId}
        messages={messages}
        onDisplayMessages={onDisplayMessages}
        onSubmitInput={onSubmitInput}
        clusterLabel={clusterLabel}
      />
    </div>
  )
}

const getServerSideProps: GetServerSideProps<
  TesesJuridicasChatPageProps,
  TesesJuridicasChatPageParams
> = async (ctx) => {
  const { id: clusterId } = ctx.params ?? {}
  if (!clusterId) {
    return {
      notFound: true,
    }
  }

  const filters: FilterInput[] = getClusterQueryFilter(clusterId)

  const variables: TesesJuridicasChatRootQueryVariables = {
    filters,
    limit: CHAT_VERSION_DOCUMENT_LIMIT,
  }
  const { client, withApolloProps } = withApollo(ctx)
  const { data } = await client.query<TesesJuridicasChatRootQuery>({
    query: TesesJuridicasChatRoot,
    variables,
  })
  if (data?.root?.searchHaystack?.hits === 0 || data?.root?.searchHaystack?.items?.length === 0) {
    return {
      notFound: true,
    }
  }
  const clusterLabel = getClusterLabel(data, clusterId)

  if (!clusterLabel) {
    return {
      notFound: true,
    }
  }

  const canonicalUrl = `https://ia.jusbrasil.com.br/p/teses-juridicas/${clusterId}`
  return {
    props: {
      ...withApolloProps,
      ...withUserSession(ctx),
      data,
      clusterLabel,
      clusterId,
      canonicalUrl,
    },
  }
}

function getClusterLabel(data: TesesJuridicasChatRootQuery, clusterId: string) {
  const documentFromCluster = data?.root?.searchHaystack?.items?.[0]?.data as JurisprudenceMetadata
  return documentFromCluster?.addons?.legalThesisClusterLabels?.find(
    (label) => label.clusterId === clusterId
  )?.clusterLabel
}

export const TesesJuridicasChatPageExports = { getServerSideProps }
